"use client";

import { useRouter, useSearchParams } from "next/navigation";
import Link, { type LinkProps } from "next/link";
import type {
  CollaboratorsOnLists,
  FollowersOnLists,
  GoogleImage,
  List,
  Place,
  PlaceAffiliate,
  Spot,
  SpotImage,
  Tag,
  Trip,
  TripDay,
  TripItem,
  User,
  UserSpotFav,
  UserSpotVisit,
} from "@prisma/client";
import SpotView from "@/components/v3/SpotView";
import ListView from "@/components/v3/ListView";
import React from "react";
import { HideScroll } from "@/components/v3/HideScrollBar";
import { CheckIcon } from "@/app/(v3)/icons/CheckIcon";
import { PageWithNavBar } from "@/components/v3/PageWithNavBar";
import { NameMicBlack } from "@/app/(v3)/icons/NameMicBlack";
import { SearchIcon } from "@/app/(v3)/icons/SearchIcon";

export const Discover = ({
  results,
  currentUser,
  tags,
  currentTag,
}: {
  results: {
    type: "spot" | "list";
    spot:
      | (Spot & {
          images: SpotImage[];
          place: Place & { googleImages: GoogleImage[]; affiliates: PlaceAffiliate[] };
          list: List & { user: User; spots: Spot[] };
          user: User;
        })
      | null;
    list:
      | (List & {
          user: User;
          collaborators: (CollaboratorsOnLists & { user: User })[];
          spots: (Spot & { place: Place & { googleImages: GoogleImage[] } })[];
        })
      | null;
  }[];
  currentUser:
    | (User & {
        lists: (List & { spots: Spot[] })[];
        followingLists: FollowersOnLists[];
        collaboratingLists: CollaboratorsOnLists[];
        favedSpots: UserSpotFav[];
        visitedSpots: UserSpotVisit[];
        trips: (Trip & {
          tripDays: (TripDay & {
            items: (TripItem & {
              spot: Spot | null;
            })[];
          })[];
        })[];
      })
    | null;
  tags: Tag[];
  currentTag: string;
}): JSX.Element => {
  const router = useRouter();

  const searchParams = useSearchParams();

  const replaceTagParam = (tag: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("tag", tag);
    return params.toString();
  };

  const replaceSearchParam = (search: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("q", search);
    return params.toString();
  };

  const defaultQuery = searchParams.get("q") ?? "";

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const query = formData.get("search-word") as string;

    if (query) {
      router.push(`/discover?${replaceSearchParam(query)}`);
    } else {
      router.push(`/discover`);
    }
  };

  return (
    <PageWithNavBar>
      <div className="flex min-h-full flex-col bg-variable-collection-neutral-200 pb-6 pt-14">
        {/*         <div className="mb-6 flex items-center justify-between">
          <div className="text-header2">Discover</div>
          <div className="flex rounded-full bg-white">
            <div className="flex h-[38px] w-[38px] items-center justify-center rounded-full bg-variable-collection-neutral-900">
              <LocationIcon className="h-5 w-5" color="white" />
            </div>
            <div className="flex h-[38px] w-[38px] items-center justify-center rounded-full">
              <Link href="/guides">
                <ContractIcon className="opacity-30" />
              </Link>
            </div>
          </div>
        </div> */}

        <div className="px-5">
          <div className="mb-4 flex items-center gap-3 rounded-full bg-white px-4 py-2.5">
            <SearchIcon className="opacity-50" />
            <form onSubmit={onSubmit} className="flex-1">
              <input
                type="text"
                name="search-word"
                defaultValue={defaultQuery}
                placeholder="What do you want to see?"
                className="w-full border-none px-0 py-1.5 font-medium opacity-50 focus:ring-0"
              />
            </form>
            <button type="button" className="opacity-50">
              <NameMicBlack />
            </button>
          </div>
        </div>

        <HideScroll className="mb-6 h-[34px]">
          <div className="flex w-max gap-2.5 px-5 text-sm font-medium">
            <TagComp href={`/discover?${replaceTagParam("all")}`} isActive={currentTag === "all"} tagName="All" />
            {tags.map((tag) => {
              const isActive = currentTag === tag.slug;
              return (
                <TagComp
                  key={tag.id}
                  href={`/discover?${replaceTagParam(tag.slug)}`}
                  isActive={isActive}
                  tagName={tag.name}
                />
              );
            })}
          </div>
        </HideScroll>

        <div className="flex flex-col gap-4 px-5 md:grid md:grid-cols-[repeat(auto-fill,minmax(353px,1fr))]">
          {results.map((result) => (
            <>
              {result.type === "spot" && (
                <SpotView
                  key={result.spot!.id}
                  currentUser={currentUser}
                  spot={result.spot!}
                  linkUrl={`/guides/${result.spot!.list.slug}#${result.spot!.slug}`}
                  showListTitle={true}
                />
              )}
              {result.type === "list" && <ListView key={result.list!.id} list={result.list!} />}
            </>
          ))}
        </div>
      </div>
    </PageWithNavBar>
  );
};

interface TagProps extends Pick<LinkProps<unknown>, "href"> {
  isActive: boolean;
  tagName: string;
}

const TagComp = ({ href, isActive, tagName }: TagProps) => (
  <Link
    href={href}
    prefetch={false}
    className={`flex items-center gap-1.5 rounded-lg px-3 py-2 capitalize leading-tight ${isActive ? "bg-black text-white" : "bg-white"}`}
  >
    {tagName} {isActive && <CheckIcon />}
  </Link>
);
